import React, {useEffect, useState} from "react";
import WebPageLayout from "../../../../../layouts/WebPageLayout";
import TablePageLayout from "../../../../../layouts/TablePageLayout";
import {Button, DataTable} from "react-native-paper";
import {MaterialIcons} from "@expo/vector-icons";
import {
  useDeleteAssetMutation,
  useLazyExportAssetsListQuery,
  useLazyGetAssetsListQuery
} from "../../../../../../../store/services/api/asset-api";
import { ExportOutlined, SearchOutlined } from "@ant-design/icons";
import {BaseFilters} from "../../../../../../web/hooks/useFilters";
import {CoreTable} from "../../../../../components/CoreTable";
import SystemAssetsScreenFilters from "./components/SystemAssetsScreenFilters";
import {convertBase64toBlob} from "../../../../../../../utils/helpers";
import {useToast} from "../../../../../../../components/toast/ToastProvider";
import {useSetLoading} from "../../../../../../../hooks/useSetLoading";
import {getSystemScreenHeaders} from "../../../system-screen-headers";
import useEntityListPage from "../../../../../hooks/useEntityListPage";
import useHasPermission from "../../../../../../../hooks/useHasPermission";
import WorkOrdersListAssetModal from "../../../../work-orders/screens/list/workOrdersListAssetModal";
import WorkOrderAssetOpenWorkOrdersModal from "../../../../../../../components/modals/WorkOrderAssetOpenWorkOrdersModal";
import {Button as AntButton} from "antd";
import AssetAllWorkOrdersModal from "../../../../../../../components/modals/AssetAllWorkOrdersModal";
import { WorkOrderStatus, WorkOrderType } from "../../../../../../../store/data/entities/work-order";
import {useExcelExport} from "../../../../../hooks/useExport";
import ColumnExportModal, { ExportableColumn } from "../../../../../../../components/modals/ColumnExportModal";
import { Animated, TouchableOpacity } from "react-native";
import { useRef } from "react";

interface AssetListFilters extends BaseFilters {
  buildingId?: string[];
  floorId?: string[];
  assetTypeId?: string[];
  subAssetTypeId?: string[];
  description?: string;
  code?: string;
  uniqueReference?: string;
  WOReference?: string;
  ticketStatus?: number;
  createdOnStartDate?: string;
  createdOnEndDate?: string;
  noWorkOrdersOperator?: string;
  createdByUserId?: string[];
  noWorkOrders?: number;
  statuses?: WorkOrderStatus[];
  type?: WorkOrderType[];
}

export default function SystemAssetsListScreen() {
  const {hasPermission} = useHasPermission();

  const [filtersVisible, setFiltersVisible] = useState(true);
  const filtersWidth = useRef(new Animated.Value(filtersVisible ? 1 : 0)).current;

  const toggleFilters = () => {
  const toValue = filtersVisible ? 0 : 1;
  Animated.timing(filtersWidth, {
      toValue,
      duration: 300,
      useNativeDriver: false, 
    }).start();
    setFiltersVisible(!filtersVisible);
  };

  const exportableColumns: ExportableColumn[] = [
    { key: "BuildingName", label: "Building" },
    { key: "FloorName", label: "Floor" },
    { key: "LocationName", label: "Location" },
    { key: "WorkOrderCount", label: "No of Work Orders" },
    { key: "AssetTypeName", label: "Asset Type" },
    { key: "SubAssetTypeName", label: "Sub Asset Type" },
    { key: "Description", label: "Description" },
    { key: "Code", label: "Code" },
    { key: "UniqueReference", label: "CoreRFID QR" },
    { key: "supportingInformation", label: "Supporting Information" },
    { key: "priority", label: "Ranking" },
    { key: "ImageUri", label: "Image" },
  ];

  const { exportExcel, isLoading: exportLoading } = useExcelExport({
    query: useLazyExportAssetsListQuery,
  });
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  const {
    navigateToCreate,
    navigateToEdit,
    onSubmitFilter,
    filter,
    tableData,
    isLoading,
    deleteEntity
  } = useEntityListPage<AssetListFilters>({
    entityName: "Asset",
    lazyTableQuery: useLazyGetAssetsListQuery,
    deleteEntityMutation: useDeleteAssetMutation,
    filterDefinition: {
      ticketStatus: -1,
      sortedColumn: '',
      sortedColumnAscending: false,
      pageNumber: 1,
      pageSize: 50
    },
  });

  useSetLoading([exportLoading]);

  return (
    <WebPageLayout header={getSystemScreenHeaders()}>


      <TablePageLayout
        filters={
          <Animated.View
            style={{
              overflow: 'hidden',
              width: filtersWidth.interpolate({
                inputRange: [0, 1],
                outputRange: [0, 300], 
              }),
              height: '100%',
            }}
          >
            <SystemAssetsScreenFilters filter={filter} onSubmit={onSubmitFilter} />
          </Animated.View>
        }

        actions={(
          <>
          <Button
            icon={filtersVisible ? "chevron-left" : "chevron-right"}
            mode="contained-tonal"
            onPress={toggleFilters}
            style={{ marginRight: 8 }}
          >
            {filtersVisible ? "Hide Filters" : "Show Filters"}
          </Button>
            
            {hasPermission('CanAddAssets') && (
              <Button icon='plus' mode="elevated" onPress={navigateToCreate} buttonColor='#fff' textColor='#0f0f0f'>
                Add Asset
              </Button>
            )}

            {tableData?.list.length > 0 && (
              <AntButton
                size='large'
                icon={<ExportOutlined />}
                loading={exportLoading}
                onClick={() => setExportModalOpen(true)}
              >
                Export Excel
              </AntButton>
            )}
          </>
        )}
      >
        <CoreTable<any>
          headers={[
            { text: 'Building', sortedName: "buildingName", flex: 1 },
            { text: 'Floor', sortedName: "floorName", flex: 0.75},
            { text: 'Specific Location', sortedName: "locationName", flex: 1 },
            { text: 'No of Work Orders', sortedName: "workOrderCount", flex: 0.5 },
            { text: 'Asset Type', sortedName: "assetTypeName", flex: 0.75},
            { text: 'Sub Asset Type', sortedName: "subAssetTypeName", flex: 1},
            { text: 'Description', sortedName: "description", flex: 1},
            { text: 'Code', sortedName: "code", flex: 1},
            { text: 'CoreRFID QR', sortedName: "uniqueReference", flex: 1 },
            { text: 'Options', flex: 0.5},
          ]}
          isLoading={isLoading}
          data={tableData}
          keyExtractor={(item) => item.id}
          renderItem={({item}) => (
            <CoreTable.Row key={item.id} style={{}}>
              <CoreTable.Cell style={{flex: 1}}>{item.buildingName}</CoreTable.Cell>
              <CoreTable.Cell style={{flex: 0.75}}>{item.floorName}</CoreTable.Cell>
              <CoreTable.Cell style={{flex: 1}}>{item.locationName}</CoreTable.Cell>
              <CoreTable.Cell  style={{flex: 0.5}}>
                {item.workOrderCount > 0 ? (
                  <AssetAllWorkOrdersModal
                    assetId={item.id}
                    workOrderReference={filter.filters.WOReference}
                    workOrderStatuses={filter.filters.statuses}
                    workOrderTypes={filter.filters.type}
                    triggerContent={<AntButton type="link" size="small">{item.workOrderCount}</AntButton>}
                  />
                ) : (
                  item.workOrderCount
                )}
              </CoreTable.Cell>
              <CoreTable.Cell style={{flex: 0.75}}>{item.assetTypeName}</CoreTable.Cell>
              <CoreTable.Cell style={{flex: 1}}>{item.subAssetTypeName}</CoreTable.Cell>
              <CoreTable.Cell style={{flex: 1}}>{item.description}</CoreTable.Cell>
              <CoreTable.Cell style={{flex: 1}}>{item.code}</CoreTable.Cell>
              <CoreTable.Cell style={{flex: 1}}>{item.uniqueReference}</CoreTable.Cell>
              <CoreTable.Cell style={{flex: 0.5}}>
                <TouchableOpacity onPress={() => navigateToEdit(item.id)}>
                  <MaterialIcons name="edit" size={24} color="black"/>
                </TouchableOpacity>

                <TouchableOpacity onPress={() => deleteEntity(item)}>
                  <MaterialIcons name="delete-outline" size={24} color="red"/>
                </TouchableOpacity>
              </CoreTable.Cell>
            </CoreTable.Row>
          )}
          filters={filter.filters}
          setFilter={filter.setFilter}
        />

        <ColumnExportModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          onConfirm={(cols) => {
            exportExcel({
              ...filter.getFilterValues(),
              columns: cols,
            });
            setExportModalOpen(false);
          }}
          columns={exportableColumns}
          selectedColumns={selectedColumns}
          setSelectedColumns={setSelectedColumns}
        />
      </TablePageLayout>
    </WebPageLayout>
  )
}
